import React, { useState, useEffect, useCallback } from 'react';
import {
  Users, BookOpen, Building2, Download, Upload, Plus,
  Search, RefreshCw, FileSpreadsheet, CheckCircle, XCircle,
  ChevronLeft, ChevronRight, Filter,
} from 'lucide-react';
import { employeesAPI } from '../services/api';
import { useAuth } from '../context/AuthContext';

// ── Stat Card ─────────────────────────────────────────────
function StatCard({ icon: Icon, label, value, color = 'blue' }) {
  const pal = {
    blue:   'from-blue-500 to-blue-600',
    green:  'from-emerald-500 to-emerald-600',
    purple: 'from-purple-500 to-purple-600',
    amber:  'from-amber-500 to-amber-600',
  };
  return (
    <div className="bg-white/80 backdrop-blur-sm rounded-2xl shadow-sm border border-white/60 p-5 flex items-center gap-4">
      <div className={`w-12 h-12 rounded-xl bg-gradient-to-br ${pal[color]} flex items-center justify-center shadow-md flex-shrink-0`}>
        <Icon size={22} className="text-white" />
      </div>
      <div>
        <p className="text-xs text-slate-500 font-medium uppercase tracking-wide">{label}</p>
        <p className="text-2xl font-bold text-slate-800">{value ?? '—'}</p>
      </div>
    </div>
  );
}

// ── Badge ─────────────────────────────────────────────────
function Badge({ val }) {
  if (val === 'teacher')       return <span className="inline-flex items-center gap-1 text-xs font-medium bg-emerald-50 text-emerald-700 px-2 py-0.5 rounded-full">Teaching</span>;
  if (val === 'non-teaching')  return <span className="inline-flex items-center gap-1 text-xs font-medium bg-amber-50 text-amber-700 px-2 py-0.5 rounded-full">Non-Teaching</span>;
  return <span className="text-xs text-slate-400">{val}</span>;
}

const PAGE_SIZE = 20;

export default function Teachers() {
  const { user } = useAuth();
  const isAdmin  = user?.role === 'admin';

  // State
  const [teachers, setTeachers]       = useState([]);
  const [filtered, setFiltered]       = useState([]);
  const [departments, setDepartments] = useState([]);
  const [loading, setLoading]         = useState(true);
  const [importing, setImporting]     = useState(false);

  // Filters
  const [search, setSearch]         = useState('');
  const [deptFilter, setDeptFilter] = useState('');
  const [typeFilter, setTypeFilter] = useState('teacher'); // 'teacher' | 'non-teaching' | ''
  const [page, setPage]             = useState(1);

  // Import feedback
  const [importResult, setImportResult] = useState(null);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const [empRes, deptRes] = await Promise.all([
        employeesAPI.getAll({ staffType: typeFilter || undefined }),
        employeesAPI.getDepartments(),
      ]);
      setTeachers(empRes.data);
      setDepartments(deptRes.data);
    } catch(e) { console.error(e); }
    finally { setLoading(false); }
  }, [typeFilter]);

  useEffect(() => { fetchData(); }, [fetchData]);

  // Client-side filter+search
  useEffect(() => {
    let rows = [...teachers];
    if (search)     rows = rows.filter(r => r.name.toLowerCase().includes(search.toLowerCase()) ||
                                           (r.email||'').toLowerCase().includes(search.toLowerCase()) ||
                                           (r.subject||'').toLowerCase().includes(search.toLowerCase()));
    if (deptFilter) rows = rows.filter(r => r.department === deptFilter);
    setFiltered(rows);
    setPage(1);
  }, [teachers, search, deptFilter]);

  const paginated   = filtered.slice((page-1)*PAGE_SIZE, page*PAGE_SIZE);
  const totalPages  = Math.ceil(filtered.length / PAGE_SIZE);

  // Stats
  const totalTeachers    = teachers.filter(t=>t.staff_type==='teacher').length;
  const totalNonTeaching = teachers.filter(t=>t.staff_type==='non-teaching').length;
  const totalActive      = teachers.filter(t=>t.is_active).length;
  const uniqueDepts      = [...new Set(teachers.map(t=>t.department).filter(Boolean))].length;

  // Export
  const handleExport = async () => {
    try {
      const res = await employeesAPI.export({ staffType: typeFilter || undefined });
      const url = URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement('a'); a.href = url;
      a.download = typeFilter === 'teacher' ? 'teachers_export.xlsx' : 'staff_export.xlsx';
      a.click();
    } catch(e) { alert('Export failed'); }
  };

  // Download template
  const handleTemplate = async () => {
    try {
      const res = await employeesAPI.teacherTemplate();
      const url = URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement('a'); a.href = url; a.download = 'teacher_import_template.xlsx'; a.click();
    } catch(e) { alert('Template download failed'); }
  };

  // Import
  const handleImport = async e => {
    const file = e.target.files[0];
    if (!file) return;
    setImporting(true);
    setImportResult(null);
    const fd = new FormData(); fd.append('file', file);
    try {
      const res = await employeesAPI.import(fd, typeFilter || 'teacher');
      setImportResult({ success: true, ...res.data });
      fetchData();
    } catch(err) {
      setImportResult({ success: false, message: err.response?.data?.error || err.message });
    }
    finally { setImporting(false); e.target.value = ''; }
  };

  return (
    <div className="space-y-6">

      {/* ── Header ── */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-slate-800">Teachers &amp; Staff</h1>
          <p className="text-sm text-slate-500 mt-0.5">{filtered.length} record{filtered.length!==1?'s':''} · Use Excel to bulk upload or update</p>
        </div>
        <div className="flex flex-wrap gap-2">
          {isAdmin && (
            <>
              <button onClick={handleTemplate}
                className="flex items-center gap-2 px-4 py-2 bg-white border border-slate-200 text-slate-700 text-sm font-medium rounded-xl hover:bg-slate-50 transition">
                <FileSpreadsheet size={16} /> Sample Template
              </button>
              <label className={`flex items-center gap-2 px-4 py-2 bg-emerald-600 text-white text-sm font-medium rounded-xl hover:bg-emerald-700 cursor-pointer transition ${importing?'opacity-60 pointer-events-none':''}`}>
                <Upload size={16} /> {importing ? 'Importing…' : 'Upload Excel'}
                <input type="file" accept=".xlsx,.xls" className="hidden" onChange={handleImport} />
              </label>
            </>
          )}
          <button onClick={handleExport}
            className="flex items-center gap-2 px-4 py-2 bg-white border border-slate-200 text-slate-700 text-sm font-medium rounded-xl hover:bg-slate-50 transition">
            <Download size={16} /> Export
          </button>
          <button onClick={fetchData}
            className="flex items-center gap-2 px-4 py-2 bg-primary-600 text-white text-sm font-medium rounded-xl hover:bg-primary-700 transition">
            <RefreshCw size={16} className={loading?'animate-spin':''} /> Refresh
          </button>
        </div>
      </div>

      {/* ── Import Result Banner ── */}
      {importResult && (
        <div className={`flex items-start gap-3 p-4 rounded-xl border text-sm ${importResult.success ? 'bg-emerald-50 border-emerald-200 text-emerald-800' : 'bg-red-50 border-red-200 text-red-800'}`}>
          {importResult.success ? <CheckCircle size={18} className="text-emerald-600 flex-shrink-0 mt-0.5" /> : <XCircle size={18} className="text-red-600 flex-shrink-0 mt-0.5" />}
          <div>
            <p className="font-medium">{importResult.message}</p>
            {importResult.success && importResult.importedUsers > 0 && (
              <p className="text-emerald-700 text-xs mt-0.5">✅ {importResult.importedUsers} dashboard login account{importResult.importedUsers!==1?'s':''} created</p>
            )}
            {importResult.errors?.length > 0 && (
              <ul className="mt-1 list-disc list-inside text-xs space-y-0.5">
                {importResult.errors.map((e,i) => <li key={i}>{e}</li>)}
              </ul>
            )}
          </div>
          <button onClick={()=>setImportResult(null)} className="ml-auto text-slate-400 hover:text-slate-600">✕</button>
        </div>
      )}

      {/* ── Stat Cards ── */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <StatCard icon={Users}     label="Teaching Staff"     value={totalTeachers}    color="blue"   />
        <StatCard icon={BookOpen}  label="Non-Teaching"       value={totalNonTeaching} color="amber"  />
        <StatCard icon={CheckCircle} label="Active"           value={totalActive}      color="green"  />
        <StatCard icon={Building2} label="Departments"        value={uniqueDepts}      color="purple" />
      </div>

      {/* ── How to Import Guide ── */}
      <div className="bg-blue-50 border border-blue-200 rounded-2xl p-5">
        <h3 className="text-sm font-semibold text-blue-800 mb-2 flex items-center gap-2">
          <FileSpreadsheet size={16} /> How to Upload Teachers via Excel
        </h3>
        <ol className="text-sm text-blue-700 space-y-1 list-decimal list-inside">
          <li>Click <strong>"Sample Template"</strong> to download the Excel template with correct column headers</li>
          <li>Fill in teacher data — required: <code className="bg-blue-100 px-1 rounded">Name</code>, optional: <code className="bg-blue-100 px-1 rounded">Employee Code</code>, <code className="bg-blue-100 px-1 rounded">Email</code>, <code className="bg-blue-100 px-1 rounded">Subject</code>, <code className="bg-blue-100 px-1 rounded">Department</code>, <code className="bg-blue-100 px-1 rounded">Designation</code>, <code className="bg-blue-100 px-1 rounded">Phone</code>, <code className="bg-blue-100 px-1 rounded">Qualification</code>, <code className="bg-blue-100 px-1 rounded">Join Date</code></li>
          <li>To give a teacher a dashboard login: set <code className="bg-blue-100 px-1 rounded">Create Login</code> = <strong>Yes</strong> (needs Email). Default password: <code className="bg-blue-100 px-1 rounded">Admin@123</code></li>
          <li>If a teacher already exists (same <code className="bg-blue-100 px-1 rounded">Employee Code</code>), they will be <strong>updated</strong> — not duplicated</li>
          <li>Click <strong>"Upload Excel"</strong> and select your filled file</li>
        </ol>
      </div>

      {/* ── Filters ── */}
      <div className="bg-white/80 backdrop-blur-sm rounded-2xl shadow-sm border border-white/60 p-4">
        <div className="flex flex-wrap gap-3 items-end">
          <div className="relative flex-1 min-w-[200px]">
            <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
            <input type="text" placeholder="Search name, email, subject…" value={search}
              onChange={e=>{setSearch(e.target.value);setPage(1);}}
              className="w-full pl-8 pr-3 py-2 text-sm border border-slate-200 rounded-lg bg-white focus:outline-none focus:ring-2 focus:ring-primary-500" />
          </div>
          <div className="flex flex-col gap-1">
            <label className="text-xs font-medium text-slate-500 uppercase tracking-wide">Staff Type</label>
            <select value={typeFilter} onChange={e=>{setTypeFilter(e.target.value);setPage(1);}}
              className="text-sm border border-slate-200 rounded-lg px-3 py-2 bg-white focus:outline-none focus:ring-2 focus:ring-primary-500">
              <option value="">All Staff</option>
              <option value="teacher">Teaching Staff</option>
              <option value="non-teaching">Non-Teaching</option>
            </select>
          </div>
          <div className="flex flex-col gap-1">
            <label className="text-xs font-medium text-slate-500 uppercase tracking-wide">Department</label>
            <select value={deptFilter} onChange={e=>{setDeptFilter(e.target.value);setPage(1);}}
              className="text-sm border border-slate-200 rounded-lg px-3 py-2 bg-white focus:outline-none focus:ring-2 focus:ring-primary-500">
              <option value="">All Departments</option>
              {departments.map(d => <option key={d.id} value={d.name}>{d.name}</option>)}
            </select>
          </div>
          {(search || deptFilter || typeFilter) && (
            <button onClick={()=>{setSearch('');setDeptFilter('');setTypeFilter('teacher');setPage(1);}}
              className="flex items-center gap-1 text-sm text-slate-500 hover:text-slate-700 px-3 py-2 rounded-lg border border-slate-200 bg-white hover:bg-slate-50 transition">
              <Filter size={14} /> Clear
            </button>
          )}
        </div>
      </div>

      {/* ── Data Table ── */}
      <div className="bg-white/80 backdrop-blur-sm rounded-2xl shadow-sm border border-white/60 overflow-hidden">
        <div className="px-5 py-3 border-b border-slate-100 flex items-center justify-between">
          <h2 className="font-semibold text-slate-700">Staff Directory</h2>
          <span className="text-xs text-slate-400">{filtered.length} total</span>
        </div>

        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-slate-500 text-xs font-medium uppercase tracking-wide">
              <tr>
                <th className="px-4 py-3 text-left">#</th>
                <th className="px-4 py-3 text-left">Name</th>
                <th className="px-4 py-3 text-left">Employee Code</th>
                <th className="px-4 py-3 text-left">Subject</th>
                <th className="px-4 py-3 text-left">Department</th>
                <th className="px-4 py-3 text-left">Designation</th>
                <th className="px-4 py-3 text-left">Type</th>
                <th className="px-4 py-3 text-left">Email / Phone</th>
                <th className="px-4 py-3 text-left">Status</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-50">
              {loading ? (
                <tr><td colSpan={9} className="px-4 py-8 text-center text-slate-400">Loading…</td></tr>
              ) : paginated.length === 0 ? (
                <tr><td colSpan={9} className="px-4 py-8 text-center text-slate-400">
                  {teachers.length === 0 ? 'No staff records yet. Upload an Excel file to get started.' : 'No records match your filter.'}
                </td></tr>
              ) : paginated.map((t, i) => (
                <tr key={t.id} className="hover:bg-slate-50/80 transition-colors">
                  <td className="px-4 py-3 text-slate-400">{(page-1)*PAGE_SIZE+i+1}</td>
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-2">
                      <div className="w-8 h-8 rounded-full bg-primary-100 flex items-center justify-center flex-shrink-0">
                        <span className="text-primary-700 font-semibold text-xs">{t.name?.charAt(0)}</span>
                      </div>
                      <div>
                        <p className="font-medium text-slate-700">{t.name}</p>
                        {t.qualification && <p className="text-xs text-slate-400">{t.qualification}</p>}
                      </div>
                    </div>
                  </td>
                  <td className="px-4 py-3 text-slate-500 font-mono text-xs">{t.employee_code || '—'}</td>
                  <td className="px-4 py-3 text-slate-600">{t.subject || '—'}</td>
                  <td className="px-4 py-3 text-slate-600">{t.department || '—'}</td>
                  <td className="px-4 py-3 text-slate-600">{t.designation || '—'}</td>
                  <td className="px-4 py-3"><Badge val={t.staff_type} /></td>
                  <td className="px-4 py-3">
                    <p className="text-slate-600">{t.email || '—'}</p>
                    {t.phone && <p className="text-xs text-slate-400">{t.phone}</p>}
                  </td>
                  <td className="px-4 py-3">
                    {t.is_active
                      ? <span className="inline-flex items-center gap-1 text-xs font-medium bg-emerald-50 text-emerald-700 px-2 py-0.5 rounded-full">Active</span>
                      : <span className="inline-flex items-center gap-1 text-xs font-medium bg-red-50 text-red-700 px-2 py-0.5 rounded-full">Inactive</span>}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="px-5 py-3 border-t border-slate-100 flex items-center justify-between text-sm text-slate-500">
            <span>Showing {(page-1)*PAGE_SIZE+1}–{Math.min(page*PAGE_SIZE, filtered.length)} of {filtered.length}</span>
            <div className="flex items-center gap-2">
              <button disabled={page===1} onClick={()=>setPage(p=>p-1)}
                className="p-1.5 rounded-lg hover:bg-slate-100 disabled:opacity-40 transition">
                <ChevronLeft size={16} />
              </button>
              <span className="font-medium text-slate-700">{page} / {totalPages}</span>
              <button disabled={page===totalPages} onClick={()=>setPage(p=>p+1)}
                className="p-1.5 rounded-lg hover:bg-slate-100 disabled:opacity-40 transition">
                <ChevronRight size={16} />
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
